// ============================================
// COMPONENT - CrearSucursalModal
// ============================================

import { useState } from 'react';
import { MdClose } from 'react-icons/md';
import { useCrearSucursal, useUIStore } from '@almacenes/core';
import type { CrearSucursalDto } from '@almacenes/core';
import { useRegiones, useComunas } from '../../../hooks/useRegiones';

interface CrearSucursalModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CrearSucursalModal = ({ isOpen, onClose }: CrearSucursalModalProps) => {
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    numeroDireccion: '',
    regionCodigo: '',
    comunaId: '',
  });

  const { data: regiones, isLoading: isLoadingRegiones } = useRegiones();
  const { data: comunas, isLoading: isLoadingComunas } = useComunas(
    formData.regionCodigo ? Number(formData.regionCodigo) : null
  );

  const crearSucursalMutation = useCrearSucursal();
  const addToast = useUIStore((state) => state.addToast);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validación
    if (!formData.nombre.trim()) {
      addToast('error', 'El nombre es obligatorio');
      return;
    }
    if (!formData.direccion.trim()) {
      addToast('error', 'La dirección es obligatoria');
      return;
    }
    if (!formData.numeroDireccion || Number(formData.numeroDireccion) <= 0) {
      addToast('error', 'El número de dirección es obligatorio y debe ser mayor a 0');
      return;
    }
    if (!formData.comunaId) {
      addToast('error', 'Debe seleccionar una comuna');
      return;
    }

    const datos: CrearSucursalDto = {
      nombre: formData.nombre.trim(),
      direccion: formData.direccion.trim(),
      numeroDireccion: Number(formData.numeroDireccion),
      comunaId: Number(formData.comunaId),
    };

    try {
      await crearSucursalMutation.mutateAsync(datos);
      addToast('success', 'Sucursal creada correctamente');
      setFormData({ nombre: '', direccion: '', numeroDireccion: '', regionCodigo: '', comunaId: '' });
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al crear la sucursal';
      addToast('error', errorMessage);
    }
  };

  const handleClose = () => {
    setFormData({ nombre: '', direccion: '', numeroDireccion: '', regionCodigo: '', comunaId: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
            Nueva Sucursal
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={crearSucursalMutation.isPending}
          >
            <MdClose className="text-2xl" style={{ color: '#11194F' }} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              placeholder="Ingrese el nombre de la sucursal"
              disabled={crearSucursalMutation.isPending}
            />
          </div>

          {/* Dirección */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Dirección <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.direccion}
              onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              placeholder="Ingrese la dirección"
              disabled={crearSucursalMutation.isPending}
            />
          </div>

          {/* Número de Dirección */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Número <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.numeroDireccion}
              onChange={(e) => setFormData({ ...formData, numeroDireccion: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              placeholder="Ingrese el número de dirección"
              min="1"
              disabled={crearSucursalMutation.isPending}
            />
          </div>

          {/* Región */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Región <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.regionCodigo}
              onChange={(e) =>
                setFormData({ ...formData, regionCodigo: e.target.value, comunaId: '' })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              disabled={crearSucursalMutation.isPending || isLoadingRegiones}
            >
              <option value="">Seleccione una región</option>
              {regiones?.map((region) => (
                <option key={region.codigo} value={region.codigo}>
                  {region.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Comuna */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#11194F' }}>
              Comuna <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.comunaId}
              onChange={(e) => setFormData({ ...formData, comunaId: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent transition-all"
              disabled={
                crearSucursalMutation.isPending ||
                !formData.regionCodigo ||
                isLoadingComunas
              }
            >
              <option value="">
                {!formData.regionCodigo
                  ? 'Primero seleccione una región'
                  : isLoadingComunas
                  ? 'Cargando comunas...'
                  : 'Seleccione una comuna'}
              </option>
              {comunas?.map((comuna) => (
                <option key={comuna.id} value={comuna.id}>
                  {comuna.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              style={{ color: '#11194F' }}
              disabled={crearSucursalMutation.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-[#43BFFF] text-white rounded-lg font-medium hover:bg-[#3AABEB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={crearSucursalMutation.isPending}
            >
              {crearSucursalMutation.isPending && (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              )}
              {crearSucursalMutation.isPending ? 'Creando...' : 'Crear Sucursal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
